import { Fragment } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Hecho } from "@/lib/generacion";
import {
  dividirEnCitas,
  resolverCita,
  type CitaResuelta,
  type EstadoDeLaCita,
  type SegmentoDeTexto,
  type Verificacion,
} from "@/lib/redaccion";
import { cn } from "@/lib/utils";

/**
 * Subrayado de la cita. Verde punteado cuando cuadra con la hoja de hechos,
 * ámbar cuando el valor escrito no coincide con el de la hoja, rojo cuando la
 * clave no existe. El color acompaña a un texto (tooltip y panel de
 * verificación): nunca es lo único que dice que algo anda mal.
 */
const ESTILO_DE_LA_CITA: Record<EstadoDeLaCita, string> = {
  ok: "decoration-primary font-semibold underline decoration-dotted decoration-[1.5px] underline-offset-4",
  no_cuadra: "bg-warning-soft border-warning-dot rounded-[0.25rem] border-b-2 px-0.5 font-semibold",
  desconocida: "bg-danger-soft border-danger-dot rounded-[0.25rem] border-b-2 px-0.5 font-semibold",
};

interface TextoConCitasProps {
  texto: string;
  verificacion: Verificacion | null;
  hechos: Hecho[];
}

/**
 * La prosa de una sección tal como se lee: el valor de cada cita subrayado y,
 * al pasar el cursor o enfocarla, de qué hecho viene. Las marcas
 * `[[valor|clave]]` NUNCA se muestran fuera del modo de edición.
 */
export function TextoConCitas({ texto, verificacion, hechos }: TextoConCitasProps) {
  const parrafos = texto
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p !== "");

  return (
    <div className="flex max-w-[47.5rem] flex-col gap-3">
      {parrafos.map((parrafo, i) => (
        <p key={i} className="text-ink-body text-sm leading-[1.7] text-pretty whitespace-pre-line">
          {dividirEnCitas(parrafo).map((segmento, j) =>
            segmento.tipo === "texto" ? (
              <Fragment key={j}>{segmento.texto}</Fragment>
            ) : (
              <Cita key={j} cita={segmento} resuelta={resolverCita(segmento, verificacion, hechos)} />
            ),
          )}
        </p>
      ))}
    </div>
  );
}

function Cita({ cita, resuelta }: { cita: Extract<SegmentoDeTexto, { tipo: "cita" }>; resuelta: CitaResuelta }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} data-estado={resuelta.estado} className={cn("cursor-help", ESTILO_DE_LA_CITA[resuelta.estado])}>
          {cita.valor === "" ? "(cita vacía)" : cita.valor}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-pretty">{explicacion(resuelta)}</TooltipContent>
    </Tooltip>
  );
}

/** «Hecho · valor · fuente», o por qué la cita no cuadra. */
function explicacion(r: CitaResuelta): string {
  const nombre = r.hecho ? `${r.hecho.nombre}${r.hecho.sujeto_etiqueta ? ` (${r.hecho.sujeto_etiqueta})` : ""}` : null;

  if (r.estado === "no_cuadra") {
    return `No cuadra: la hoja de hechos dice ${r.esperado ?? "otro valor"}${nombre ? ` · ${nombre}` : ""}`;
  }
  if (r.estado === "desconocida") return r.mensaje ?? "Esta cita no corresponde a ningún hecho de la hoja.";
  if (r.hecho === null) return "Cita sin datos en la hoja de hechos cargada.";

  return `${nombre} · ${r.hecho.valor_formateado ?? "—"} · ${r.hecho.fuente.texto ?? "sin fuente"}`;
}
